"use client"

import { useState, useEffect, useContext, useCallback, useRef, Fragment, useMemo } from "react"
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  Typography,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Fade,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Slide,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import DeleteIcon from "@mui/icons-material/Delete"
import { ErpContext } from "../../context/ErpContext.js"
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt"
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"
import InventoryIcon from "@mui/icons-material/Inventory"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PriceCheckIcon from "@mui/icons-material/PriceCheck"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner"
import DescriptionIcon from "@mui/icons-material/Description"
import PersonIcon from "@mui/icons-material/Person"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import { getLocaleConfig } from "../../utils/numeros.js"
import NumericInput from "../misc/NumericInput.js"

// Componente de transición para las alertas
function SlideTransition(props) {
  return <Slide {...props} direction="up" />
}

/**
 * 1) ENCABEZADO: se rellenan datos únicos del item:
 *    código, código de barras (scanner), descripción, y atributos.
 * 2) Al hacer clic en "Confirmar/Crear Ítem", se muestra el bloque con las 3 tablas.
 */
export default function ItemWithThreeTables() {
  const {
    getUbicacion,
    ubicaciones,
    getEntidadByTipoEntidad,
    entidadesTipoEntidad,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    itemAtributoNoEliminados,
    getItemAtributoNoEliminados,
    postItem,
    postItemUbicacion,
    postListaMonto,
    getUsuario,
    usuarios,
    getItemByCodigoItem,
    getItemSuggestions,
    getEntidadByID,
    isNavbarCollapsed,
    getParametrosGlobales,
    getValoresUnicosAtributo,
    getValoresUnicosAtributoRecientes,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  const theme = useTheme()
  const localeConfig = useMemo(() => getLocaleConfig(formatoNumericoGlobal || null), [formatoNumericoGlobal])

  // -- Estados del encabezado
  const [codigoItem, setCodigoItem] = useState("")
  const [codigoScannerItem, setCodigoScannerItem] = useState("")
  const [descripcionItem, setDescripcionItem] = useState("")
  const [pluBalanzaItem, setPluBalanzaItem] = useState("")
  const [codigoSuggestions, setCodigoSuggestions] = useState([])
  const [descripcionSuggestions, setDescripcionSuggestions] = useState([])
  const [scannerSuggestions, setScannerSuggestions] = useState([])
  const [searchTextCliente, setSearchTextCliente] = useState("") // Texto de búsqueda
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null) // Usuario seleccionado
  const [openModal, setOpenModal] = useState(false)

  // Atributos globales (hasta 10) que se completan una sola vez
  const [atributosForm, setAtributosForm] = useState([])
  const [sugerenciasAtributos, setSugerenciasAtributos] = useState({})
  const highlightedAtributoRef = useRef({})

  // Manejo del "Item Creado" para mostrar las tablas
  const [isItemCreated, setIsItemCreated] = useState(false)

  // Estados para alertas y carga
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState("")
  const [alertSeverity, setAlertSeverity] = useState("success")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [entidadCreatedAlert, setEntidadCreatedAlert] = useState(false)
  const [entidadMessage, setEntidadMessage] = useState("")
  const [parametrosGlobales, setParametrosGlobales] = useState({})
  const [usaGestionLotes, setUsaGestionLotes] = useState(false)

  const showPluField = parametrosGlobales.pluBalanza === "1"
  const headerColumnSize = showPluField ? 3 : 4

  // Estado derivado para ubicaciones seleccionadas en la tabla de stock
  const [stockUbicaciones, setStockUbicaciones] = useState([])

  // -------------------------------------
  //  TABLA #1: STOCK
  // -------------------------------------
  const [stockRows, setStockRows] = useState([])

  // Efecto para actualizar las ubicaciones disponibles basadas en la tabla de stock
  useEffect(() => {
    // Extraer las ubicaciones seleccionadas de las filas de stock
    const ubicacionesSeleccionadas = stockRows
      .filter((row) => row.ubicacion) // Filtrar solo las filas con ubicación seleccionada
      .map((row) => row.ubicacion) // Obtener los IDs de ubicación
      .filter((id, index, self) => self.indexOf(id) === index) // Eliminar duplicados

    // Actualizar el estado de ubicaciones disponibles para las otras tablas
    setStockUbicaciones(ubicacionesSeleccionadas)
  }, [stockRows])

  // Efecto para cargar datos iniciales
  useEffect(() => {
    async function fetchData() {
      try {
        await getUbicacion()
        await getUsuario()
        await getEntidadByTipoEntidad(2)
        await getEntidadesClientesTresLetras("")
        await getItemAtributoNoEliminados()
        const parametros = await getParametrosGlobales(["pluBalanza", "gestionDeLotes", "ubicacionDefaultCreacionItems"])
        if (parametros) {
          setParametrosGlobales(parametros)
        }
      } catch (error) {
        console.error("Error al cargar datos:", error)
        showAlert("Error al cargar datos iniciales", "error")
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1) // Llama a la API
    }
  }, [searchTextCliente]) //Este paramatro searchText condiciona al useEffect, detectando sus cambios. Solo se ejecuta si cambia el valor del input de searchText.

  // Al cambiar itemAtributoNoEliminados, inicializar atributosForm
  useEffect(() => {
    if (itemAtributoNoEliminados?.length > 0) {
      const inicial = itemAtributoNoEliminados.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        valor: "",
      }))
      setAtributosForm(inicial)
    }
  }, [itemAtributoNoEliminados])

  // Manejar cambio en atributos del encabezado
  const handleAtributoFormChange = (id, valor) => {
    setAtributosForm((prev) => prev.map((attr) => (attr.id === id ? { ...attr, valor } : attr)))
  }

  const cargarSugerenciasAtributo = async (campo, searchText = "") => {
    const len = (searchText || "").trim().length
    try {
      let valores = []
      if (len === 0 && getValoresUnicosAtributoRecientes) {
        valores = await getValoresUnicosAtributoRecientes(campo, "", 10)
      } else if (len >= 1 && len <= 3 && getValoresUnicosAtributoRecientes) {
        valores = await getValoresUnicosAtributoRecientes(campo, searchText, 30)
      } else if (len >= 4 && getValoresUnicosAtributo) {
        valores = await getValoresUnicosAtributo(campo, searchText)
      }
      setSugerenciasAtributos((prev) => ({ ...prev, [campo]: valores }))
    } catch (error) {
      console.error(`Error sugerencias atributo ${campo}:`, error)
    }
  }

  const moveFocusToNext = useCallback(() => {
    const focusables = document.querySelectorAll(
      'input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    const current = document.activeElement
    const idx = Array.from(focusables).indexOf(current)
    if (idx >= 0 && idx < focusables.length - 1) {
      focusables[idx + 1].focus()
    }
  }, [])

  // Función para mostrar alertas
  const showAlert = (message, severity = "success") => {
    setAlertMessage(message)
    setAlertSeverity(severity)
    setAlertOpen(true)
  }

  const updateSuggestionList = useCallback((field, items) => {
    const formatted = items.map((item) => ({
      id: item.id,
      codigo: item.codigo || "",
      descripcion: item.descripcion || "",
      codigoScanner: item.codigoScanner || "",
    }))

    switch (field) {
      case "codigo":
        setCodigoSuggestions(formatted)
        break
      case "descripcion":
        setDescripcionSuggestions(formatted)
        break
      case "codigoScanner":
        setScannerSuggestions(formatted)
        break
      default:
        break
    }
  }, [])

  const fetchItemSuggestions = useCallback(
    async (field, value) => {
      if (!getItemSuggestions) return
      const trimmed = value.trim()

      if (trimmed.length < 2) {
        updateSuggestionList(field, [])
        return
      }

      try {
        const response = await getItemSuggestions(field, trimmed)
        const items = response?.items || []
        updateSuggestionList(field, items)
      } catch (error) {
        console.error("Error obteniendo sugerencias:", error)
        updateSuggestionList(field, [])
      }
    },
    [getItemSuggestions, updateSuggestionList],
  )

  useEffect(() => {
    fetchItemSuggestions("codigo", codigoItem)
  }, [codigoItem, fetchItemSuggestions])

  useEffect(() => {
    fetchItemSuggestions("descripcion", descripcionItem)
  }, [descripcionItem, fetchItemSuggestions])

  useEffect(() => {
    fetchItemSuggestions("codigoScanner", codigoScannerItem)
  }, [codigoScannerItem, fetchItemSuggestions])

  const renderSuggestions = (items, title, fieldToHighlight) => {
    if (!items || items.length === 0) return null

    const highlightStyle = (field) => ({
      fontWeight: field === fieldToHighlight ? 700 : 400,
      color: field === fieldToHighlight ? theme.palette.primary.main : theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    })

    return (
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Typography variant="overline" sx={{ letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "150px 1fr 150px",
            columnGap: 1.5,
            rowGap: 0.75,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
            Código
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
            Descripción
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
            Código scanner
          </Typography>

          {items.map((item) => (
            <Fragment key={`${title}-${item.id}-${item.codigo}`}>
              <Typography variant="caption" sx={highlightStyle("codigo")} title={item.codigo || "—"}>
                {item.codigo || "—"}
              </Typography>
              <Typography variant="caption" sx={highlightStyle("descripcion")} title={item.descripcion || "—"}>
                {item.descripcion || "—"}
              </Typography>
              <Typography variant="caption" sx={highlightStyle("codigoScanner")} title={item.codigoScanner || "—"}>
                {item.codigoScanner || "—"}
              </Typography>
            </Fragment>
          ))}
        </Box>
      </Box>
    )
  }

  // Función para confirmar la creación del Ítem
  // (ej: validas si se llenó código, scanner, descripción)
  const handleCrearItem = async () => {
    if (!codigoItem || !codigoScannerItem || !descripcionItem) {
      showAlert("Por favor completa Código, Código Scanner y Descripción.", "warning")
      return
    }

    try {
      const existeItem = await getItemByCodigoItem(codigoItem)
      if (existeItem) {
        showAlert("Ya existe un ítem con ese código", "error")
        return
      }

      // Se marca como creado para mostrar las 3 tablas
      setIsItemCreated(true)
      showAlert("Ítem confirmado. Complete las tablas para finalizar.", "info")
    } catch (error) {
      console.error("Error al verificar el código:", error)
      showAlert("Error al verificar el código del ítem", "error")
    }
  }

  // Función para resetear el formulario completo
  const resetForm = () => {
    // Resetear campos del encabezado
    setCodigoItem("")
    setCodigoScannerItem("")
    setDescripcionItem("")
    setPluBalanzaItem("")
    setUsuarioSeleccionado(null)
    setUsaGestionLotes(false)
    setCodigoSuggestions([])
    setDescripcionSuggestions([])
    setScannerSuggestions([])

    // Resetear atributos
    if (itemAtributoNoEliminados?.length > 0) {
      const resetAtributos = itemAtributoNoEliminados.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        valor: "",
      }))
      setAtributosForm(resetAtributos)
    }

    // Resetear tablas
    setStockRows([])
    setCostRows([])
    setPriceRows([])

    // Resetear estado de creación
    setIsItemCreated(false)
  }

  // Agregar fila
  const handleAddStockRow = () => {
    const idDefault = parametrosGlobales?.ubicacionDefaultCreacionItems
    const idNum = idDefault ? parseInt(idDefault, 10) : null
    const ubicacionDefault = idNum && ubicaciones?.some((u) => u.id === idNum) ? idNum : null

    setStockRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: ubicacionDefault,
        inventario: "",
        stockMin: "",
      },
    ])
  }
  // Copiar fila
  const handleCopyStockRow = (rowId) => {
    setStockRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      const copia = { ...original, id: Date.now() }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeleteStockRow = (rowId) => {
    setStockRows((prev) => {
      const rowToDelete = prev.find((r) => r.id === rowId)
      const ubicacionEliminada = rowToDelete?.ubicacion
      const newRows = prev.filter((r) => r.id !== rowId)

      if (newRows.length === 0) {
        setCostRows([])
        setPriceRows([])
      } else if (ubicacionEliminada) {
        setCostRows((c) => c.filter((r) => r.ubicacion !== ubicacionEliminada))
        setPriceRows((p) => p.filter((r) => r.ubicacion !== ubicacionEliminada))
      }

      return newRows
    })
  }
  // Agregar 1 fila de costo (idEntidad 2) y 1 de precio (idEntidad 1) cuando se selecciona una ubicación nueva
  const addDefaultCostAndPriceForUbicacion = async (ubicacionId) => {
    setCostRows((prev) => {
      if (prev.some((r) => r.ubicacion === ubicacionId)) return prev
      return [
        ...prev,
        { id: Date.now(), ubicacion: ubicacionId, proveedor: 2, costo: "", margenGanancia: "", esProveedorReferente: true },
      ]
    })
    let clienteGenerico = null
    try {
      const defaultEntidad = await getEntidadByID(1)
      if (defaultEntidad) {
        clienteGenerico = {
          label: `${defaultEntidad.descripcion} - ${defaultEntidad.apellido} - ${defaultEntidad.dniCuitCuil}`,
          id: defaultEntidad.id,
        }
      }
    } catch (error) {
      console.error("Error al obtener cliente genérico:", error)
    }
    if (!clienteGenerico && entidadesClientesTresLetras?.length > 0) {
      const p = entidadesClientesTresLetras[0]
      clienteGenerico = {
        label: `${p.descripcion || ""} - ${p.apellido || ""} - ${p.dniCuitCuil || ""}`.trim(),
        id: p.id,
      }
    }
    setPriceRows((prev) => {
      if (prev.some((r) => r.ubicacion === ubicacionId)) return prev
      return [
        ...prev,
        {
          id: Date.now(),
          ubicacion: ubicacionId,
          cliente: clienteGenerico ? clienteGenerico.id : null,
          clienteData: clienteGenerico,
          precio: "",
        },
      ]
    })
  }

  // Manejar cambios
  const handleChangeStockRow = (rowId, field, value) => {
    setStockRows((prev) => {
      const newRows = prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
            }
          : row,
      )

      // Si se cambió una ubicación
      if (field === "ubicacion") {
        const ubicacionesSeleccionadas = newRows
          .filter((row) => row.ubicacion)
          .map((row) => row.ubicacion)
          .filter((id, index, self) => self.indexOf(id) === index)

        // Eliminar costos/precios de ubicaciones que ya no están en stock
        setCostRows((prev) =>
          prev.filter((row) => !row.ubicacion || ubicacionesSeleccionadas.includes(row.ubicacion))
        )
        setPriceRows((prev) =>
          prev.filter((row) => !row.ubicacion || ubicacionesSeleccionadas.includes(row.ubicacion))
        )

        // Si se seleccionó una ubicación nueva, agregar 1 costo (idEntidad 2) y 1 precio (idEntidad 1)
        if (value) {
          addDefaultCostAndPriceForUbicacion(value)
        }
      }

      return newRows
    })
  }

  // -------------------------------------
  //  TABLA #2: COSTOS
  // -------------------------------------
  const [costRows, setCostRows] = useState([])
  // Agregar fila
  // Modificar la función handleAddCostRow
  const handleAddCostRow = () => {
    // Solo permitir agregar filas si hay ubicaciones en stock
    if (stockRows.length === 0) {
      showAlert("Primero debe agregar ubicaciones en la tabla de Stock", "warning")
      return
    }

    setCostRows((prev) => {
      const ubicacionDefault = stockUbicaciones.length === 1 ? stockUbicaciones[0] : null
      const yaHayReferenteEnUbicacion = ubicacionDefault
        ? prev.some((r) => r.ubicacion === ubicacionDefault && r.esProveedorReferente)
        : false
      return [
        ...prev,
        {
          id: Date.now(),
          ubicacion: ubicacionDefault,
          proveedor: 2,
          costo: "",
          margenGanancia: "",
          esProveedorReferente: ubicacionDefault ? !yaHayReferenteEnUbicacion : false,
        },
      ]
    })
  }
  // Copiar fila
  const handleCopyCostRow = (rowId) => {
    setCostRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      const copia = { 
        ...original, 
        id: Date.now(),
        proveedor: original.proveedor || 2,
        esProveedorReferente: false, // La copia nunca es referente al duplicar
      }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeleteCostRow = (rowId) => {
    setCostRows((prev) => {
      let remaining = prev.filter((r) => r.id !== rowId)
      // Cuando hay un solo proveedor por ubicación, debe ser referente por defecto
      const ubicacionesConCostos = [...new Set(remaining.map((r) => r.ubicacion).filter(Boolean))]
      for (const ubId of ubicacionesConCostos) {
        const filasEnUbicacion = remaining.filter((r) => r.ubicacion === ubId)
        if (filasEnUbicacion.length === 1 && !filasEnUbicacion[0].esProveedorReferente) {
          remaining = remaining.map((r) =>
            r.id === filasEnUbicacion[0].id ? { ...r, esProveedorReferente: true } : r
          )
        }
      }
      return remaining
    })
  }
  // Agregar fila de precio desde costo (auto-agregar cuando no hay fila de precio)
  const addPriceRowFromCost = async (ubicacion, precioCalculado) => {
    if (stockRows.length === 0) return
    let clienteGenerico = null
    try {
      const defaultEntidad = await getEntidadByID(1)
      if (defaultEntidad) {
        clienteGenerico = {
          label: `${defaultEntidad.descripcion} - ${defaultEntidad.apellido} - ${defaultEntidad.dniCuitCuil}`,
          id: defaultEntidad.id,
        }
      }
    } catch (error) {
      console.error("Error al obtener cliente genérico:", error)
    }
    if (!clienteGenerico && entidadesClientesTresLetras?.length > 0) {
      const primerCliente = entidadesClientesTresLetras[0]
      clienteGenerico = {
        label: `${primerCliente.descripcion || ""} - ${primerCliente.apellido || ""} - ${primerCliente.dniCuitCuil || ""}`.trim(),
        id: primerCliente.id,
      }
    }
    setPriceRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: ubicacion || (stockUbicaciones.length === 1 ? stockUbicaciones[0] : null),
        cliente: clienteGenerico ? clienteGenerico.id : null,
        clienteData: clienteGenerico,
        precio: precioCalculado,
      },
    ])
  }

  // Obtener margen y costo de referencia por ubicación (desde costRows, fila esProveedorReferente=true)
  const getMargenForUbicacion = (ubicacionId) => {
    const refRow = costRows.find((r) => r.ubicacion === ubicacionId && r.esProveedorReferente)
    const margen = refRow?.margenGanancia
    return margen && !isNaN(parseFloat(margen)) ? parseFloat(margen) : null
  }
  const getCostoReferenciaForUbicacion = (ubicacionId) => {
    const costsForUbicacion = costRows.filter(
      (r) => r.ubicacion === ubicacionId && r.costo && !isNaN(parseFloat(r.costo))
    )
    if (costsForUbicacion.length === 0) return null
    if (costsForUbicacion.length === 1) return parseFloat(costsForUbicacion[0].costo)
    const refRow = costsForUbicacion.find((c) => c.esProveedorReferente)
    return refRow ? parseFloat(refRow.costo) : parseFloat(costsForUbicacion[0].costo)
  }
  const hasCostosRepetidosPorUbicacion = (ubicacionId) => {
    return costRows.filter((r) => r.ubicacion === ubicacionId && r.proveedor).length > 1
  }

  // Manejar cambios en filas de costo
  const handleChangeCostRow = (rowId, field, value) => {
    setCostRows((prev) => {
      const rowAntes = prev.find((r) => r.id === rowId)
      let updated = prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))

      // Al cambiar ubicacion: si no hay referente para la nueva ubicacion, marcar esta fila
      if (field === "ubicacion" && value) {
        const yaHayReferente = updated.some((r) => r.id !== rowId && r.ubicacion === value && r.esProveedorReferente)
        if (!yaHayReferente) {
          updated = updated.map((row) => row.id === rowId ? { ...row, esProveedorReferente: true } : row)
        }
        // La ubicacion antigua puede quedar con un solo proveedor: debe ser referente
        const ubicacionAntigua = rowAntes?.ubicacion
        if (ubicacionAntigua && ubicacionAntigua !== value) {
          const filasEnUbicacionAntigua = updated.filter((r) => r.ubicacion === ubicacionAntigua)
          if (filasEnUbicacionAntigua.length === 1 && !filasEnUbicacionAntigua[0].esProveedorReferente) {
            updated = updated.map((r) =>
              r.id === filasEnUbicacionAntigua[0].id ? { ...r, esProveedorReferente: true } : r
            )
          }
        }
      }

      // Al marcar esProveedorReferente: desmarcar otros de la misma ubicacion
      if (field === "esProveedorReferente" && value === true) {
        const ubicacion = updated.find((r) => r.id === rowId)?.ubicacion
        if (ubicacion) {
          updated = updated.map((row) =>
            row.ubicacion === ubicacion && row.id !== rowId ? { ...row, esProveedorReferente: false } : row
          )
        }
      }

      return updated
    })

    // Recalcular precio solo si cambió costo, margen del referente o ubicacion
    if (field === "costo" || field === "ubicacion" || field === "margenGanancia") {
      const costoRow = costRows.find((r) => r.id === rowId)
      const ubicacion = field === "ubicacion" ? value : costoRow?.ubicacion
      if (!ubicacion) return

      // Solo recalcular precio si la fila que cambió es el referente (o si cambió la ubicacion)
      // Para filas no-referentes, el margen se guarda pero no afecta el precio calculado
      const esReferente = costoRow?.esProveedorReferente
      if (field === "margenGanancia" && !esReferente) return

      // Obtener margen: si es la fila referente, usar el nuevo valor; sino buscar en la fila referente actual
      let margen = null
      if (field === "margenGanancia" && esReferente) {
        margen = value !== "" && !isNaN(parseFloat(value)) ? parseFloat(value) : null
      } else {
        margen = getMargenForUbicacion(ubicacion)
      }
      if (margen == null) return

      let costoRef = null
      if (field === "costo" && !isNaN(parseFloat(value))) {
        costoRef = esReferente ? parseFloat(value) : getCostoReferenciaForUbicacion(ubicacion)
      } else {
        costoRef = getCostoReferenciaForUbicacion(ubicacion)
      }

      if (costoRef != null) {
        const precio = costoRef * (1 + margen / 100)
        const priceRowExistente =
          priceRows.find((pr) => pr.ubicacion === ubicacion && (pr.cliente === 1 || pr.cliente === "1")) ||
          priceRows.find((pr) => pr.ubicacion === ubicacion)
        if (priceRowExistente) {
          setPriceRows((prev) =>
            prev.map((pr) =>
              pr.ubicacion === ubicacion && pr.id === priceRowExistente.id ? { ...pr, precio } : pr
            )
          )
        } else {
          addPriceRowFromCost(ubicacion, precio)
        }
      }
    }
  }

  // -------------------------------------
  //  TABLA #3: PRECIOS (Clientes)
  // -------------------------------------
  const [priceRows, setPriceRows] = useState([])
  // Agregar fila
  // Modificar la función handleAddPriceRow
  const handleAddPriceRow = async () => {
    // Solo permitir agregar filas si hay ubicaciones en stock
    if (stockRows.length === 0) {
      showAlert("Primero debe agregar ubicaciones en la tabla de Stock", "warning")
      return
    }

    // Obtener cliente genérico automáticamente
    let clienteGenerico = null;
    try {
      const defaultEntidad = await getEntidadByID(1);
      if (defaultEntidad) {
        clienteGenerico = {
          label: `${defaultEntidad.descripcion} - ${defaultEntidad.apellido} - ${defaultEntidad.dniCuitCuil}`,
          id: defaultEntidad.id,
        };
      }
    } catch (error) {
      console.error("Error al obtener cliente genérico:", error);
    }

    setPriceRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: stockUbicaciones.length === 1 ? stockUbicaciones[0] : null, // Si solo hay una ubicación, seleccionarla automáticamente
        cliente: clienteGenerico ? clienteGenerico.id : null, // Cliente genérico por defecto (ID = 1)
        clienteData: clienteGenerico, // Datos completos del cliente para el Autocomplete
        precio: "",
      },
    ])
  }
  // Copiar fila
  const handleCopyPriceRow = async (rowId) => {
    setPriceRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      
      // Si no hay cliente seleccionado, usar cliente genérico
      let clienteId = original.cliente;
      if (!clienteId) {
        clienteId = 1; // Cliente genérico por defecto
      }
      
      const copia = { 
        ...original, 
        id: Date.now(),
        cliente: clienteId
      }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeletePriceRow = (rowId) => {
    setPriceRows((prev) => prev.filter((r) => r.id !== rowId))
  }
  // Manejar cambios
  const handleChangePriceRow = (rowId, field, value) => {
    setPriceRows((prev) => prev.map((row) => {
      if (row.id === rowId) {
        if (field === "cliente") {
          // Cuando se cambia el cliente, también actualizar clienteData
          const clienteEncontrado = entidadesClientesTresLetras.find((it) => it.id === value);
          return {
            ...row,
            [field]: value,
            clienteData: clienteEncontrado ? {
              label: `${clienteEncontrado.descripcion} - ${clienteEncontrado.apellido} - ${clienteEncontrado.dniCuitCuil}`,
              id: clienteEncontrado.id,
            } : null
          };
        }
        return { ...row, [field]: value };
      }
      return row;
    }))
  }

  const abrirModalCrearEntidad = () => {
    setOpenModal(true)
  }

  useEffect(() => {
    if (costRows.length > 0) {
      setCostRows((prev) =>
        prev.map((row) => ({
          ...row,
          proveedor: row.proveedor && entidadesTipoEntidad.find((e) => e.id === row.proveedor) ? row.proveedor : null,
        })),
      )
    }
  }, [entidadesTipoEntidad])

  // Recalcular precios cuando cambian costRows (margen y referente ahora viven en costRows)
  useEffect(() => {
    setPriceRows((prev) =>
      prev.map((pr) => {
        const margen = getMargenForUbicacion(pr.ubicacion)
        const costoRef = getCostoReferenciaForUbicacion(pr.ubicacion)
        if (margen != null && costoRef != null) {
          const precioCalculado = costoRef * (1 + margen / 100)
          return { ...pr, precio: precioCalculado }
        }
        return pr
      })
    )
  }, [costRows])

  const handleEntidadCreada = async (nuevaEntidad) => {
    try {
      const tipoEntidadId = nuevaEntidad.idTipoEntidad
      const entidadId = nuevaEntidad.id
      const entidadLabel = [nuevaEntidad.descripcion, nuevaEntidad.apellido, nuevaEntidad.dniCuitCuil]
        .filter((part) => part && part !== "")
        .join(" - ")

      if (tipoEntidadId === 2) {
        await getEntidadByTipoEntidad(2)

        // PROVEEDOR: Si la última fila de Costos no tiene proveedor, la asignamos.
        setCostRows((prev) => {
          if (prev.length > 0 && !prev[prev.length - 1].proveedor) {
            const newRows = [...prev]
            const lastRowId = newRows[newRows.length - 1].id

            newRows[newRows.length - 1] = {
              ...newRows[newRows.length - 1],
              proveedor: entidadId,
            }
            return newRows
          } else {
            // Si todas las filas ya tienen proveedor, agregamos una nueva fila con la entidad seleccionada
            return [
              ...prev,
              {
                id: Date.now(),
                ubicacion: null,
                proveedor: entidadId,
                costo: "",
              },
            ]
          }
        })
        setEntidadMessage(`Proveedor ${nuevaEntidad.descripcion} creado con éxito`)
      } else if (tipoEntidadId === 1) {
        await getEntidadesClientesTresLetras("", [1], false, false)

        // CLIENTE: Si la última fila de Precios no tiene cliente, la asignamos.
        setPriceRows((prev) => {
          if (prev.length > 0 && !prev[prev.length - 1].cliente) {
            const newRows = [...prev]
            const lastRowId = newRows[newRows.length - 1].id

            newRows[newRows.length - 1] = {
              ...newRows[newRows.length - 1],
              cliente: entidadId,
              clienteData: {
                label: entidadLabel,
                id: entidadId,
              },
            }
            return newRows
          } else {
            // Si todas las filas ya tienen cliente, agregamos una nueva fila con la entidad seleccionada
            return [
              ...prev,
              {
                id: Date.now(),
                ubicacion: null,
                cliente: entidadId,
                clienteData: {
                  label: entidadLabel,
                  id: entidadId,
                },
                precio: "",
              },
            ]
          }
        })
        setEntidadMessage(`Cliente ${nuevaEntidad.descripcion} creado con éxito`)
      } else {
        if (tipoEntidadId) {
          await getEntidadByTipoEntidad(tipoEntidadId)
          await getEntidadesClientesTresLetras("", [tipoEntidadId], false, false)
        }
        setEntidadMessage(`Entidad ${nuevaEntidad.descripcion} creada con éxito`)
      }

      // Mostrar alerta de éxito
      setEntidadCreatedAlert(true)
      setTimeout(() => setEntidadCreatedAlert(false), 4000)

      // Asegurar cierre del modal
      setOpenModal(false)
    } catch (error) {
      console.error("Error al procesar la entidad creada:", error)
      showAlert("Error al actualizar los datos de la nueva entidad", "error")
    }
  }

  const handleItemSubmit = async (e) => {
    e.preventDefault()

    // Validaciones
    if (!codigoItem || !codigoScannerItem || !descripcionItem) {
      showAlert("Por favor complete la información básica del ítem", "warning")
      return
    }

    // Validar que el código tenga al menos 3 caracteres
    if (codigoItem.trim().length < 3) {
      showAlert("El código debe tener al menos 3 caracteres", "warning")
      return
    }

    if (stockRows.length === 0) {
      showAlert("Debe agregar al menos una fila de stock", "warning")
      return
    }

    // Validar que todas las filas tengan datos completos
    const stockIncompleto = stockRows.some((row) => !row.ubicacion || row.stockMin === "")
    const costosIncompleto = costRows.some((row) => !row.ubicacion || !row.proveedor || row.costo === "")
    const preciosIncompleto = priceRows.some((row) => !row.ubicacion || !row.cliente || row.precio === "")

    if (stockIncompleto || costosIncompleto || preciosIncompleto) {
      showAlert("Hay filas con información incompleta en las tablas", "warning")
      return
    }

    // Validar proveedor referente cuando hay margen y múltiples proveedores por ubicación
    const ubicacionesConCostos = [...new Set(costRows.map((r) => r.ubicacion).filter(Boolean))]
    for (const ubicacionId of ubicacionesConCostos) {
      const costosEnUbicacion = costRows.filter((r) => r.ubicacion === ubicacionId && r.proveedor)
      if (costosEnUbicacion.length > 1) {
        const referente = costosEnUbicacion.find((r) => r.esProveedorReferente)
        if (!referente) {
          const ubDesc = ubicaciones?.find((u) => u.id === ubicacionId)?.descripcion || ubicacionId
          showAlert(`Debe seleccionar proveedor referente para la ubicación "${ubDesc}" (hay múltiples proveedores)`, "warning")
          return
        }
      }
    }

    setIsSubmitting(true)

    try {
      // console.log("Creando item...")
      const response = await postItem(
        descripcionItem,
        codigoItem,
        atributosForm,
        codigoScannerItem,
        showPluField ? pluBalanzaItem : null,
        usaGestionLotes
      )
      // Obtener el último item creado
      const newItemId = response.data.id
      // console.log("Nuevo item creado con ID: " + newItemId)

      // Crear ubicaciones de stock
      for (const itemUbicacion of stockRows) {
        const inventarioNum = Number(itemUbicacion.inventario)
        await postItemUbicacion(newItemId, itemUbicacion.ubicacion, inventarioNum, itemUbicacion.stockMin)
      }

      // Crear costos (con margenGanancia y esProveedorReferente en listaDeMontos)
      for (const costoLista of costRows) {
        const margenVal = costoLista.margenGanancia && !isNaN(parseFloat(costoLista.margenGanancia))
          ? parseFloat(costoLista.margenGanancia) : null
        await postListaMonto(
          newItemId,
          Number.parseInt(costoLista.ubicacion, 10),
          Number.parseInt(costoLista.proveedor, 10),
          Number.parseFloat(costoLista.costo),
          margenVal,
          costoLista.esProveedorReferente === true,
        )
      }

      // Crear precios
      for (const precioLista of priceRows) {
        await postListaMonto(
          newItemId,
          Number.parseInt(precioLista.ubicacion, 10), // Convierte a entero
          Number.parseInt(precioLista.cliente, 10), // Convierte a entero
          Number.parseFloat(precioLista.precio), // Convierte a float
        )
      }

      // Mostrar alerta de éxito
      showAlert(`Ítem ${codigoItem} creado exitosamente`, "success")

      // Resetear el formulario
      resetForm()
    } catch (error) {
      console.error("Error en la creación del item o ubicaciones:", error)
      showAlert("Error al crear el ítem", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* ========== ENCABEZADO ========== */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
          position: "relative",
        }}
      >
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgb(244, 147, 14)' }}>
              Crear Nuevo Ítem
            </Typography>
          }
          subheader="Complete la información básica del ítem"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        />

        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Primera fila */}
            <Grid item xs={12} md={headerColumnSize}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocalOfferIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <TextField
                  fullWidth
                  label="Código"
                  variant="outlined"
                  size="small"
                  value={codigoItem}
                  onChange={(e) => setCodigoItem(e.target.value)}
                  error={codigoItem.trim().length > 0 && codigoItem.trim().length < 3}
                  helperText={codigoItem.trim().length > 0 && codigoItem.trim().length < 3 ? "El código debe tener al menos 3 caracteres" : ""}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
              {renderSuggestions(codigoSuggestions, "Referencias recientes · Código", "codigo")}
            </Grid>

            <Grid item xs={12} md={headerColumnSize}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <QrCodeScannerIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <TextField
                  fullWidth
                  label="Código Scanner"
                  variant="outlined"
                  value={codigoScannerItem}
                  size="small"
                  onChange={(e) => setCodigoScannerItem(e.target.value)}
                  onKeyDown={(e) => {
                    // Si presionás Enter en este campo,
                    // y está vacío, pero ya hay algo en codigoItem,
                    // copiamos automáticamente el valor.
                    if (e.key === 'Enter' && !codigoScannerItem && codigoItem) {
                      setCodigoScannerItem(codigoItem)
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
              {renderSuggestions(scannerSuggestions, "Referencias recientes · Código scanner", "codigoScanner")}
            </Grid>

            <Grid item xs={12} md={headerColumnSize}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DescriptionIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <TextField
                  fullWidth
                  label="Descripción Item"
                  variant="outlined"
                  size="small"
                  value={descripcionItem}
                  onChange={(e) => setDescripcionItem(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
              {renderSuggestions(descripcionSuggestions, "Referencias recientes · Descripción", "descripcion")}
            </Grid>

            {showPluField && (
              <Grid item xs={12} md={headerColumnSize}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormatListNumberedIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                  <TextField
                    fullWidth
                    label="PLU Balanza"
                    variant="outlined"
                    size="small"
                    value={pluBalanzaItem}
                    onChange={(e) => setPluBalanzaItem(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </Grid>
            )}

            {/* <Grid item xs={12} md={2}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <Autocomplete
                  fullWidth
                  disablePortal
                  size="small"
                  options={usuarios.map((it) => ({
                    label: `${it.usuario} - ${it.nombre} - ${it.apellido}`,
                    nombre: it.nombre,
                    apellido: it.apellido,
                    id: it.id,
                    usuario: it.usuario,
                  }))}
                  value={usuarioSeleccionado}
                  onChange={(event, newValue) => {
                    setUsuarioSeleccionado(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Usuario" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                  )}
                />
              </Box>
            </Grid> */}

            {/* Atributos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Chip label="Atributos del Ítem" color="primary" variant="outlined" sx={{ fontWeight: 500, color: '#5b5b5f',
                border: '1px solid #5b5b5f', }} />
              </Divider>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                {atributosForm.map((attr, index) => (
                  <Grid item xs={12} sm={6} md={3} lg={2} key={attr.id}>
                    <Autocomplete
                      freeSolo
                      autoHighlight
                      openOnFocus
                      options={sugerenciasAtributos[`itemDatoAtributo${index + 1}`] || []}
                      value={attr.valor}
                      onHighlightChange={(event, option) => {
                        highlightedAtributoRef.current[index] = option ?? null
                      }}
                      onInputChange={(event, newValue) => {
                        handleAtributoFormChange(attr.id, newValue)
                        cargarSugerenciasAtributo(`itemDatoAtributo${index + 1}`, newValue)
                      }}
                      onChange={(event, newValue) => {
                        handleAtributoFormChange(attr.id, newValue || "")
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={attr.descripcion}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                          onFocus={() => {
                            cargarSugerenciasAtributo(`itemDatoAtributo${index + 1}`, "")
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Tab") {
                              const opciones = sugerenciasAtributos[`itemDatoAtributo${index + 1}`] || []
                              if (opciones.length > 0) {
                                event.preventDefault()
                                const valorAUsar =
                                  highlightedAtributoRef.current[index] ?? opciones[0]
                                handleAtributoFormChange(attr.id, valorAUsar)
                                setTimeout(() => moveFocusToNext(), 0)
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Checkbox para gestión de lotes - solo visible si el parámetro global está activo */}
              {parametrosGlobales?.gestionDeLotes === "1" && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={usaGestionLotes}
                        onChange={(e) => setUsaGestionLotes(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Este ítem usa gestión de lotes (FIFO por fecha de vencimiento)"
                  />
                </Box>
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Tooltip title="Crear proveedor/cliente">
              <Button
                variant="outlined"
                startIcon={<PersonAddAltIcon />}
                onClick={abrirModalCrearEntidad}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                }}
              >
                Crear Entidad
              </Button>
            </Tooltip>

            <Button
              variant="contained"
              size="large"
              onClick={handleCrearItem}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 4,
                boxShadow: 3,
                backgroundColor: '#5b5b5f', color: 'white',
                color: 'white',
              }}
            >
              Confirmar Ítem
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ========== Sección de Tablas (solo si item creado) ========== */}
      {isItemCreated && (
        <Fade in={isItemCreated} timeout={500}>
          <Box>
            <Grid container spacing={3}>
              {/* ========== TABLA 1: STOCK ========== */}
              <Grid item xs={12} md={5}>
                <Card elevation={3} sx={{ borderRadius: 2, height: "100%" }}>
                  <CardHeader
                    avatar={<InventoryIcon sx={{color:'rgb(244, 147, 14)'}} />}
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Stock
                        </Typography>
                        <Tooltip title="Agregar fila">
                          <IconButton onClick={handleAddStockRow} size="small" sx={{ ml: 1 }}>
                            <AddCircleOutlineIcon sx={{color:'rgb(244, 147, 14)'}} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    subheader={
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        Primero agregue ubicaciones aquí para habilitar las tablas de costos y precios
                      </Typography>
                    }
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  />

                  <CardContent sx={{ p: 0, height: "calc(100% - 72px)" }}>
                    <TableContainer
                      sx={{
                        height: "100%",
                        overflowY: "auto",
                        display: "block",
                      }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "40%",
                              }}
                            >
                              Ubicación
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "25%",
                              }}
                            >
                              Inventario
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "25%",
                              }}
                            >
                              Stock Mín
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "10%",
                              }}
                            >
                              Acciones
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                <Typography color="text.secondary">
                                  No hay registros. Haga clic en + para agregar.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            stockRows.map((row) => (
                              <TableRow key={row.id} hover>
                                {/* Ubicación */}
                                <TableCell>
                                  <Autocomplete
                                    size="small"
                                    options={ubicaciones || []}
                                    getOptionLabel={(opt) => opt.descripcion}
                                    value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                    onChange={(e, newValue) =>
                                      handleChangeStockRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label="Ubicación"
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                      />
                                    )}
                                    ListboxProps={{
                                      style: {
                                        maxHeight: 200,
                                      },
                                    }}
                                  />
                                </TableCell>
                                   {/* Inventario */}
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Inventario"
                                    value={row.inventario}
                                    onChange={(e) => handleChangeStockRow(row.id, "inventario", e.target.value)}
                                    variant="outlined"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.5,
                                      },
                                    }}
                                  />
                                </TableCell>
                                {/* Stock Mín */}
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Stock Mínimo"
                                    value={row.stockMin}
                                    onChange={(e) => handleChangeStockRow(row.id, "stockMin", e.target.value)}
                                    variant="outlined"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.5,
                                      },
                                    }}
                                  />
                                </TableCell>
                                {/* Acciones */}
                                <TableCell align="center">
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <Tooltip title="Copiar Fila">
                                      <IconButton onClick={() => handleCopyStockRow(row.id)} color="info" size="small">
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar Fila">
                                      <IconButton
                                        onClick={() => handleDeleteStockRow(row.id)}
                                        color="error"
                                        size="small"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={7}>
                <Grid container spacing={3}>
                  {/* ========== TABLA 2: COSTOS ========== */}
                  <Grid item xs={12}>
                    <Card
                      elevation={3}
                      sx={{
                        borderRadius: 2,
                        opacity: stockRows.length === 0 ? 0.7 : 1,
                        position: "relative",
                      }}
                    >
                      {stockRows.length === 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            backgroundColor: "rgba(255,255,255,0.7)",
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Primero agregue ubicaciones en la tabla de Stock
                          </Typography>
                        </Box>
                      )}
                      <CardHeader
                        avatar={<AttachMoneyIcon color={stockRows.length === 0 ? "disabled" : 'rgb(244, 147, 14)'} sx={{color:'rgb(244, 147, 14)'}}  />}
                        title={
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: stockRows.length === 0 ? "text.disabled" : "inherit" }}
                            >
                              Lista de Costos
                            </Typography>
                            <Tooltip
                              title={
                                stockRows.length === 0
                                  ? "Primero agregue ubicaciones en la tabla de Stock"
                                  : "Agregar fila"
                              }
                            >
                              <span>
                                <IconButton
                                  onClick={handleAddCostRow}
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1 }}
                                  disabled={stockRows.length === 0}
                                >
                                  <AddCircleOutlineIcon sx={{color:'rgb(244, 147, 14)'}} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        }
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.02 : 0.05),
                          borderBottom: `1px solid ${alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.05 : 0.1)}`,
                        }}
                      />

                      <CardContent sx={{ p: 0 }}>
                        <TableContainer sx={{ maxHeight: 250, overflowY: "auto" }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "26%",
                                  }}
                                >
                                  Ubicación
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "26%",
                                  }}
                                >
                                  Proveedor
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "18%",
                                  }}
                                >
                                  Costo
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "12%",
                                  }}
                                >
                                  Margen (%)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "15%",
                                  }}
                                >
                                  Acciones
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {costRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                      {stockRows.length === 0
                                        ? "Primero agregue ubicaciones en la tabla de Stock"
                                        : "No hay registros. Haga clic en + para agregar."}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                costRows.map((row) => (
                                  <TableRow key={row.id} hover>
                                    {/* Ubicación */}
                                    <TableCell>
                                      <Autocomplete
                                        size="small"
                                        options={ubicaciones?.filter((u) => stockUbicaciones.includes(u.id)) || []}
                                        getOptionLabel={(opt) => opt.descripcion}
                                        value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                        onChange={(e, newValue) =>
                                          handleChangeCostRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            label="Ubicación"
                                            variant="outlined"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1.5,
                                              },
                                            }}
                                          />
                                        )}
                                        ListboxProps={{
                                          style: {
                                            maxHeight: 200,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Proveedor + Checkbox Ref. (solo si hay costos repetidos) */}
                                    <TableCell>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Autocomplete
                                          size="small"
                                          options={entidadesTipoEntidad || []}
                                          getOptionLabel={(opt) => opt.descripcion}
                                          value={entidadesTipoEntidad?.find((p) => p.id === row.proveedor) || null}
                                          onChange={(e, newValue) =>
                                            handleChangeCostRow(row.id, "proveedor", newValue ? newValue.id : null)
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              size="small"
                                              label="Proveedor"
                                              variant="outlined"
                                              sx={{
                                                flex: 1,
                                                "& .MuiOutlinedInput-root": {
                                                  borderRadius: 1.5,
                                                },
                                              }}
                                            />
                                          )}
                                          ListboxProps={{
                                            style: {
                                              maxHeight: 200,
                                            },
                                          }}
                                          disabled={stockRows.length === 0}
                                          sx={{ flex: 1, minWidth: 0 }}
                                        />
                                        {hasCostosRepetidosPorUbicacion(row.ubicacion) && (
                                          <Tooltip title="Usar como proveedor referente para esta ubicación">
                                            <Checkbox
                                              size="small"
                                              checked={!!row.esProveedorReferente}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  handleChangeCostRow(row.id, "esProveedorReferente", true)
                                                }
                                              }}
                                              sx={{ p: 0.5 }}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                    {/* Costo */}
                                    <TableCell>
                                      <NumericInput
                                        fullWidth
                                        size="small"
                                        label="Costo"
                                        value={row.costo}
                                        onChange={(val) => handleChangeCostRow(row.id, "costo", val)}
                                        locale={localeConfig.locale}
                                        InputProps={{
                                          startAdornment: (
                                            <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
                                              $
                                            </Box>
                                          ),
                                        }}
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Margen (%) - editable solo en fila referente */}
                                    <TableCell>
                                      <Tooltip
                                        title={row.esProveedorReferente ? "Margen del proveedor referente (se usa para calcular precio)" : "Margen de este proveedor (solo para seguimiento)"}
                                        enterDelay={800}
                                        leaveDelay={0}
                                      >
                                        <span style={{ display: "inline-block", width: "100%" }}>
                                          <NumericInput
                                            size="small"
                                            label="Margen %"
                                            value={row.esProveedorReferente ? row.margenGanancia : getMargenForUbicacion(row.ubicacion)}
                                            onChange={(val) => {
                                              if (row.esProveedorReferente) {
                                                handleChangeCostRow(row.id, "margenGanancia", val)
                                              }
                                            }}
                                            locale={localeConfig.locale}
                                            InputProps={{ inputProps: { min: 0, max: 999, step: 0.01 } }}
                                            sx={{
                                              width: "100%",
                                              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                                              ...(!row.esProveedorReferente && { opacity: 0.7 }),
                                            }}
                                            disabled={!row.esProveedorReferente || stockRows.length === 0}
                                          />
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    {/* Acciones */}
                                    <TableCell align="center">
                                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                                        <Tooltip title="Copiar Fila">
                                          <IconButton
                                            onClick={() => handleCopyCostRow(row.id)}
                                            color="info"
                                            size="small"
                                            disabled={stockRows.length === 0}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar Fila">
                                          <IconButton
                                            onClick={() => handleDeleteCostRow(row.id)}
                                            color="error"
                                            size="small"
                                            disabled={stockRows.length === 0}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* ========== TABLA 3: PRECIOS ========== */}
                  <Grid item xs={12}>
                    <Card
                      elevation={3}
                      sx={{
                        borderRadius: 2,
                        opacity: stockRows.length === 0 ? 0.7 : 1,
                        position: "relative",
                      }}
                    >
                      {stockRows.length === 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            backgroundColor: "rgba(255,255,255,0.7)",
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Primero agregue ubicaciones en la tabla de Stock
                          </Typography>
                        </Box>
                      )}
                      <CardHeader
                        avatar={<PriceCheckIcon color={stockRows.length === 0 ? "disabled" : 'rgb(244, 147, 14)'} sx={{color:'rgb(244, 147, 14)'}} />}
                        title={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: stockRows.length === 0 ? "text.disabled" : "inherit" }}
                            >
                              Lista de Precios
                            </Typography>
                            <Tooltip title="Agregar fila">
                              <span>
                                <IconButton
                                  onClick={handleAddPriceRow}
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1, color:'rgb(244, 147, 14)' }}
                                  disabled={stockRows.length === 0}
                                >
                                  <AddCircleOutlineIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        }
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.02 : 0.05),
                          borderBottom: `1px solid ${alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.05 : 0.1)}`,
                        }}
                      />

                      <CardContent sx={{ p: 0 }}>
                        <TableContainer sx={{ maxHeight: 250, overflowY: "auto" }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "28%",
                                  }}
                                >
                                  Ubicación
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "32%",
                                  }}
                                >
                                  Cliente
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "25%",
                                  }}
                                >
                                  Precio
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: '#5b5b5f', color: 'white',
                                    width: "15%",
                                  }}
                                >
                                  Acciones
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {priceRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                      {stockRows.length === 0
                                        ? "Primero agregue ubicaciones en la tabla de Stock"
                                        : "No hay registros. Haga clic en + para agregar."}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                priceRows.map((row) => (
                                  <TableRow key={row.id} hover>
                                    {/* Ubicación */}
                                    <TableCell>
                                      <Autocomplete
                                        size="small"
                                        options={ubicaciones?.filter((u) => stockUbicaciones.includes(u.id)) || []}
                                        getOptionLabel={(opt) => opt.descripcion}
                                        value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                        onChange={(e, newValue) =>
                                          handleChangePriceRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            label="Ubicación"
                                            variant="outlined"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1.5,
                                              },
                                            }}
                                          />
                                        )}
                                        ListboxProps={{
                                          style: {
                                            maxHeight: 200,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Cliente */}
                                    <TableCell>
                                      <Autocomplete
                                        size="small"
                                        disablePortal
                                        options={entidadesClientesTresLetras.map((it) => ({
                                          label: it.descripcion + " - " + it.apellido + " - " + it.dniCuitCuil,
                                          id: it.id,
                                        }))}
                                        value={
                                          row.clienteData || 
                                          entidadesClientesTresLetras.find((it) => it.id === row.cliente) ? {
                                            label: row.clienteData ? row.clienteData.label : 
                                              `${entidadesClientesTresLetras.find((it) => it.id === row.cliente)?.descripcion} - ${entidadesClientesTresLetras.find((it) => it.id === row.cliente)?.apellido} - ${entidadesClientesTresLetras.find((it) => it.id === row.cliente)?.dniCuitCuil}`,
                                            id: row.cliente
                                          } : null
                                        }
                                        onChange={(e, newValue) =>
                                          handleChangePriceRow(row.id, "cliente", newValue ? newValue.id : null)
                                        }
                                        onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            label="Cliente"
                                            variant="outlined"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1.5,
                                              },
                                            }}
                                          />
                                        )}
                                        ListboxProps={{
                                          style: {
                                            maxHeight: 200,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Precio */}
                                    <TableCell>
                                      <NumericInput
                                        fullWidth
                                        size="small"
                                        label="Precio"
                                        value={row.precio}
                                        onChange={(val) => handleChangePriceRow(row.id, "precio", val)}
                                        locale={localeConfig.locale}
                                        InputProps={{
                                          startAdornment: (
                                            <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
                                              $
                                            </Box>
                                          ),
                                        }}
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                        disabled={stockRows.length === 0}
                                      />
                                    </TableCell>
                                    {/* Acciones */}
                                    <TableCell align="center">
                                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                                        <Tooltip title="Copiar Fila">
                                          <IconButton
                                            onClick={() => handleCopyPriceRow(row.id)}
                                            color="info"
                                            size="small"
                                            disabled={stockRows.length === 0}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar Fila">
                                          <IconButton
                                            onClick={() => handleDeletePriceRow(row.id)}
                                            color="error"
                                            size="small"
                                            disabled={stockRows.length === 0}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Botón para guardar todo */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleItemSubmit}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <InventoryIcon />}
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 5,
                      py: 1.5,
                      boxShadow: 3,
                    }}
                  >
                    {isSubmitting ? "Procesando..." : "Crear Item Completo"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Modal para crear entidad */}
      <CrearEntidadClienteModalComponent
        open={openModal}
        handleClose={() => setOpenModal(false)}
        entidad={null}
        isEditing={false}
        onClienteCreado={handleEntidadCreada}
        tipoEntidad="proveedor"
        tipoEntidadReadonly={false}
        handleAlerta={() => {}}
        tituloPersonalizado="Crear Nueva Entidad"
      />

      {/* Alerta principal */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Alerta de entidad creada */}
      <Snackbar
        open={entidadCreatedAlert}
        autoHideDuration={4000}
        onClose={() => setEntidadCreatedAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          icon={<CheckCircleIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            backgroundColor: theme.palette.success.main,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {entidadMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
